/* **The kind of task, as a glyph in its own ink** — KANON "Karty zadań" (owner, 23–24 September
 * 2026, `docs/design/karty-zadan-2026-09-24/cards.css` `.k-glyph`).
 *
 * Every kind wears its OWN glyph colour on a neutral tile, and the colour is on the STROKE — never a
 * wash under the whole card. Eleven of the thirteen inks are colours the app already holds (a plan
 * category, an exercise type, `--danger`, `--accent`); `--task-ink-teal` and `--task-ink-sand` are
 * the mock's two new ones. The kind's NAME is on the tile as its accessible label, so the ink is
 * never the only channel.
 *
 * `wash` is the Pulpit list's tile (zk `.zh-row > .k-glyph`): the same ink, and 16% of it under the
 * glyph, because a tile in a list of ten reads the kind by its colour field first.
 *
 * The sizes are the boxes each list already drew before the task cards (owner, 26 September 2026:
 * the Pulpit's 40px tile read as a slab beside its two lines): the `/zadania` index's 28px at 8px,
 * the Pulpit's 32px `.ti` at 9px, the kinds screen's 34px at 10px. A washed tile takes its size's
 * corner; a plain one keeps the card heading's 11px. */

.tile {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--tile-size);
  height: var(--tile-size);
  color: var(--reason-ink, var(--text));
  background: var(--bg-chip);
  border-radius: var(--radius-11px);
}

.wash {
  --tile-wash: color-mix(in srgb, var(--reason-ink, var(--text)) 16%, transparent);

  background: var(--tile-wash);
  border-radius: var(--tile-wash-radius, var(--radius-12px));
}

.mark {
  width: var(--mark-size);
  height: var(--mark-size);
}

.xs {
  --tile-size: 24px;
  --mark-size: 12px;
}

/* The `/zadania` index. */
.sm {
  --tile-size: 28px;
  --mark-size: 13px;
  --tile-wash-radius: var(--radius-8px);
}

/* A card's heading on the Przegląd. */
.md {
  --tile-size: 30px;
  --mark-size: 14px;
}

/* The Pulpit's "Zadania" list. */
.lg {
  --tile-size: 32px;
  --mark-size: 15px;
  --tile-wash-radius: var(--radius-mark);
}

/* The desk's card heading and the kinds screen. */
.xl {
  --tile-size: 34px;
  --mark-size: 16px;
  --tile-wash-radius: var(--radius-sm);
}

.sessionAwaitingReview { --reason-ink: var(--accent); }
.surveyAwaitingReview { --reason-ink: var(--cat-bodyweight); }
.newClip { --reason-ink: var(--ex-plyometric); }
.clipCommentAwaitingReply { --reason-ink: var(--pop-blue); }
.missedWorkout { --reason-ink: var(--danger); }
.lapsedActivity { --reason-ink: var(--cat-hiit); }
.abandonedWorkout { --reason-ink: var(--cat-hiit); }
.noUpcomingSessions { --reason-ink: var(--cat-cardio); }
.planEndingSoon { --reason-ink: var(--task-ink-teal); }
.planCompletionDue { --reason-ink: var(--cat-hybrid); }
.planCompleted { --reason-ink: var(--text); }
.goalRenewalDue { --reason-ink: var(--cat-flexibility); }
.goalAchieved { --reason-ink: var(--cat-flexibility); }
.invitationPending { --reason-ink: var(--task-ink-sand); }
.unreadMessage { --reason-ink: var(--text-3); }

/* The first generation (attention-queue R26), in series colours the app already holds (owner's
 * colour semantics, 16 September 2026): blue is sleep, ember is energy, lime is a record. Ember as a
 * MARK is `--energy-mark`, the ember's darker shade per theme: the raw ember measures 2.6:1 on its
 * own wash in Flame. Not `--accent-text`, which is the same shade until a trainer picks a brand
 * colour and is then the brand's — energy stays ember. */
.lowSleep { --reason-ink: var(--chart-series-sleep); }
.lowEnergy { --reason-ink: var(--energy-mark); }
.prAchieved { --reason-ink: var(--chart-series-record); }
